import { useState, useEffect, useMemo, useCallback } from 'react';
import Button from '@mui/material/Button';
import PrintOutlined from '@mui/icons-material/PrintOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import axios from 'axios';
import { EmptyState, StatCard, type DataTableColumn } from '@routes-plan/ui';
import { formatCurrency } from '../../utils/format/currency';
import OfficeCashBoxesModal from './modals/OfficeCashBoxesModal';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { useT } from '../../i18n';
import { setPageTitle } from '../../utils/pageTitle';
import { ReportPage, ReportResults, ReportStats, ReportTable, RowAction } from './components/ReportLayout';

interface BoxRow {
  id: number;
  officeId: number;
  office: string;
  box: number;
  principalBox: number;
}

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const CashBoxes: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const t = useT();
  const [datas, setDatas] = useState<BoxRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState<{ id: number; name: string } | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const { canRead } = useResourcePermissions('/reports/cash-boxes');


  setPageTitle('cashReports.boxes.title');

  // Solo al montar: showAlert/showLoading/hideLoading en App no son estables (nuevo render ⇒ nueva
  // referencia) y al depender de ellas el efecto se re-ejecutaba tras showLoading(), dejando el
  // loading colgado o lanzando peticiones duplicadas. Mismo patrón que admin/offices/list.
  useEffect(() => {
    const fetchUserBoxes = async () => {
      showLoading();
      try {
        const response = await axios.get('/reports/cash-boxes/mine');
        const boxes = response.data.data;

        const list = Array.isArray(boxes) ? boxes : [];
        const transformed = [...list]
          .sort((a: any, b: any) =>
            String(a?.officeName ?? '').localeCompare(String(b?.officeName ?? ''), 'es', {
              sensitivity: 'base',
            }),
          )
          .map((box: any, index: number) => ({
            id: index + 1,
            officeId: box.officeId,
            office: box.officeName || '',
            box: box.cashBoxTotal || 0,
            principalBox: box.principalCashBoxBalance || 0,
          }));

        setDatas(transformed);

        if (!transformed.length) {
          showAlert(t('cashReports.boxes.noBoxes'), 'info');
        }
      } catch (error) {
        console.error('Error al obtener cajas:', error);
        showAlert(t('cashReports.boxes.loadError'), 'error');
      } finally {
        hideLoading();
        setLoaded(true);
      }
    };

    fetchUserBoxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks desde App.tsx (ver comentario arriba)
  }, []);

  const handleOpenModal = useCallback((row: { officeId: number; office: string }) => {
    setSelectedOffice({ id: row.officeId, name: row.office });
    setOpenModal(true);
  }, []);

  const handleCloseModal = () => {
    setSelectedOffice(null);
    setOpenModal(false);
  };

  const totalBox = datas.reduce((sum, row) => sum + Number(row.box || 0), 0);
  const totalPrincipal = datas.reduce((sum, row) => sum + Number(row.principalBox || 0), 0);

  const tableColumns = useMemo<DataTableColumn<BoxRow>[]>(
    () => [
      { id: 'office', header: t('cashReports.boxes.office'), value: (row) => row.office, sortable: true },
      { id: 'box', header: t('cashReports.boxes.box'), value: (row) => Number(row.box || 0), render: (row) => formatCurrency(Number(row.box || 0)), align: 'right', sortable: true },
      { id: 'principalBox', header: t('cashReports.boxes.mainBox'), value: (row) => Number(row.principalBox || 0), render: (row) => formatCurrency(Number(row.principalBox || 0)), align: 'right', sortable: true },
    ],
    [t],
  );

  return (
    <ReportPage
      title={t('cashReports.boxes.title')}
      description={t('cashReports.boxes.description')}
      actions={
        <Button variant="outlined" color="inherit" startIcon={<PrintOutlined />} onClick={() => window.print()} data-tour="cash-boxes-print">
          {t('cashReports.boxes.print')}
        </Button>
      }
    >
      {datas.length > 0 && (
        <ReportStats tourId="cash-boxes-stats">
          <StatCard label={t('cashReports.boxes.officeCount')} value={datas.length} />
          <StatCard label={t('cashReports.boxes.totalBox')} value={formatCurrency(totalBox)} />
          <StatCard label={t('cashReports.boxes.totalMainBox')} value={formatCurrency(totalPrincipal)} />
        </ReportStats>
      )}

      <ReportResults>
        <ReportTable<BoxRow>
          rows={datas}
          columns={tableColumns}
          getRowId={(row) => row.id}
          loading={!loaded}
          exportFileName={t('cashReports.boxes.exportFileName')}
          tourId="cash-boxes"
          ariaLabel={t('cashReports.boxes.title')}
          emptyState={<EmptyState dense title={t('cashReports.boxes.noBoxes')} />}
          rowActions={(row) => (
            <RowAction
              allowed={canRead}
              deniedTooltip={t('cashReports.boxes.noPermissionView')}
              label={t('cashReports.boxes.viewOfficeBoxes')}
              icon={<VisibilityOutlined fontSize="small" />}
              onClick={() => handleOpenModal({ officeId: Number(row.officeId), office: String(row.office ?? '') })}
            />
          )}
        />
      </ReportResults>

      <OfficeCashBoxesModal
        open={openModal && Boolean(selectedOffice)}
        onClose={handleCloseModal}
        officeId={selectedOffice?.id ?? 0}
        officeName={selectedOffice?.name ?? ''}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
    </ReportPage>
  );
};

export default CashBoxes;
